// Horizon admin shell: sidebar + fixed navbar + routed content + footer.
import { Routes, useLocation } from "react-router-dom";
import { Box, useColorModeValue, useDisclosure } from "@chakra-ui/react";
import routes from "routes.js";
import { PendingTransactionsBadgeProvider, usePendingTransactionsBadge }
  from "contexts/PendingTransactionsBadgeContext";
import useInactivityLogout from "tools/useInactivityLogout";
import { useUserRight, useScrollToTop } from "horizon/hooks";
import { getActiveRouteName, renderRoutes } from "horizon/routeUtils";
import Sidebar from "horizon/components/sidebar/Sidebar";
import Navbar from "horizon/components/navbar/Navbar";
import Footer from "horizon/components/footer/Footer";

/**
 * Inner admin shell: wires data hooks, renders the sidebar/navbar/footer chrome
 * and the routed page content. Expects badge context to be available.
 * @returns {React.ReactElement}
 */
function AdminShell() {
  const location = useLocation();
  const userRight = useUserRight();
  const { count } = usePendingTransactionsBadge();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const bg = useColorModeValue("secondaryGray.300", "transparent");
  const badges = { "/transactions": count };
  const brandText = getActiveRouteName(routes, location.pathname);
  useScrollToTop();
  useInactivityLogout();
  return (
    <Box minH="100vh" bg={bg}>
      <Sidebar routes={routes} userRight={userRight} badges={badges}
        isOpen={isOpen} onClose={onClose} />
      <Box ml={{ base: "0", xl: "300px" }} minH="100vh"
        transition="margin .2s linear">
        <Navbar brandText={brandText} onOpenSidebar={onOpen} />
        <Box px={{ base: "16px", md: "30px" }} pt={{ base: "92px", md: "104px" }} pb="20px">
          <Routes>{renderRoutes(routes)}</Routes>
        </Box>
        <Footer />
      </Box>
    </Box>
  );
}

/**
 * Admin layout entry point: provides the pending-transactions badge context to
 * the shell so the sidebar can show live counts.
 * @returns {React.ReactElement}
 */
export default function AdminLayout() {
  return (
    <PendingTransactionsBadgeProvider>
      <AdminShell />
    </PendingTransactionsBadgeProvider>
  );
}
